import { Spinner } from '@finviz/website'
import classnames from 'classnames'
import throttle from 'lodash.throttle'
import * as React from 'react'

import { ChartConfigChartPane, CustomSpineEvents } from '../../../types/shared'
import { IndicatorType } from '../../constants/common'
import { getChartQuoteFetchBufferConfig } from '../../helpers/getGetChartBarsBuffer'
import PricePerformance from '../../indicators/perf'
import { useModelState } from '../../model-hooks/use-model-state'
import Chart from '../../models/chart'
import ChartLayout from '../../models/chart_layout'
import { CHART_BARS_BUFFER_IN_PX } from '../../models/constants'
import MouseModel from '../../models/mouse'
import Quote from '../../models/quote'
import { QuoteFetchType } from '../../models/quote/constants'
import { getBarWidthWithMargin } from '../../utils/chart'
import { useIsMounted } from '../../utils/use-is-mounted'
import { getLeftOffset, getMinMaxLeftOffset } from '../renderUtils'

interface LoadLatestDataButtonProps {
  chartModel: Chart
}

const INDICATOR_OVERLAY_CHANGE_EVENTS = `${CustomSpineEvents.OverlaysChange} ${CustomSpineEvents.IndicatorsChange}`
const CHANGE_EVENT = 'change'
const DEFAULT_BARS_BUFFER_CACHE = { key: '', isLoadAll: false, bufferInPx: 0 }

export function MainChartDataLoader({ chartModel: chartModelUnwatched }: LoadLatestDataButtonProps) {
  const getIsMounted = useIsMounted()
  const chartModel = useModelState(chartModelUnwatched, { watchProperties: ['quote'] })
  const quoteModel = useModelState(chartModel.quote(), { watchProperties: ['fetchingState', 'hasReachedEnd'] })
  const wrapperRef = React.useRef<HTMLDivElement>(null)
  const lastZoomAndScrollRef = React.useRef({ zoomFactor: chartModel.zoomFactor, leftOffset: chartModel.leftOffset })
  const perfIndicatorsCacheRef = React.useRef<{
    perfIndicatorTickers: string[]
    perfIndicatorInstances: PricePerformance[]
  }>({ perfIndicatorTickers: [], perfIndicatorInstances: [] })
  const barsBufferCache = React.useRef(DEFAULT_BARS_BUFFER_CACHE)

  React.useEffect(() => {
    if (chartModelUnwatched.getAllQuotes().every((quote) => quote.hasReachedEnd)) {
      return
    }

    const checkAndLoadOlderData = ({
      chartLayout,
      chartModel,
      mainQuote,
      barWidth,
    }: {
      chartModel: Chart
      chartLayout: ChartLayout
      mainQuote: Quote
      barWidth: number
    }) => {
      const { perfIndicatorTickers, perfIndicatorInstances } = perfIndicatorsCacheRef.current

      chartModel.getAllQuotes().forEach((quote) => {
        const isTickerInChart =
          chartModel.ticker === quote.getRawTicker() || perfIndicatorTickers.includes(quote.ticker)
        const isChartTimeframe = chartModel.timeframe === quote.timeframe
        if (quote.hasReachedEnd || quote.hasError || !isTickerInChart || !isChartTimeframe) {
          return
        }

        const isMainQuote = quote.eql(mainQuote)
        const firstBarIndex = isMainQuote ? mainQuote.barIndex[0] : mainQuote.getPositionXFromTimestamp(quote.date[0])
        const scrolledInBuffer = barsBufferCache.current.bufferInPx + chartModel.leftOffset + firstBarIndex * barWidth
        if (
          (barsBufferCache.current.isLoadAll || scrolledInBuffer >= 0) &&
          !quote.getIsFetching([QuoteFetchType.Refetch, QuoteFetchType.DataChangeRefetch, QuoteFetchType.Older])
        ) {
          const lastScrollZoom = lastZoomAndScrollRef.current
          const hasScrolled = chartModel.isScrolled && lastScrollZoom.leftOffset !== chartModel.leftOffset
          const hasZoomed = lastScrollZoom.zoomFactor !== chartModel.zoomFactor
          if (isMainQuote && (hasScrolled || hasZoomed)) {
            window.gtag?.('event', 'load-previous-data', { event_label: chartLayout.specificChartFunctionality })
          }
          const oldMaxBarIndex = quote.barIndex[quote.open.length - 1]

          void quote
            .fetchOlderData(
              barsBufferCache.current.isLoadAll
                ? undefined
                : { barsCount: Math.max(Math.ceil(scrolledInBuffer / barWidth), 100) }
            )
            ?.then(() => {
              if (!getIsMounted()) {
                return
              }
              const chartModelsUsingTheQuote = chartModel
                .chart_layout()
                .getAllCharts()
                // Chart quote can be null if chart has just mounted and quote object is not available yet (loading), or chart is unmounting
                .filter((chart) => chart.quote()?.eql(quote))

              chartModelsUsingTheQuote.forEach((chartModel) => {
                const newQuote = chartModel.quote()
                const newBarWidth = getBarWidthWithMargin({
                  chartLayout: chartModel.chart_layout(),
                  zoomFactor: chartModel.zoomFactor,
                })
                const oldMaxLeftOffset = oldMaxBarIndex * newBarWidth
                const lastBarIndex = newQuote.barIndex[newQuote.open.length - 1]
                const newMaxLeftOffset = lastBarIndex * newBarWidth

                chartModel.updateAttributes({
                  leftOffset: chartModel.isScrolled
                    ? getMinMaxLeftOffset({
                        newLeftOffset: chartModel.leftOffset - (newMaxLeftOffset - oldMaxLeftOffset),
                        chartModel,
                        barIndex: lastBarIndex,
                      })
                    : getLeftOffset({
                        quote,
                        chartModel,
                      }),
                })

                chartModel.getAllPanes().forEach((pane) => {
                  if (MouseModel.position && MouseModel.pane()?.eql(pane)) {
                    MouseModel.updateAttributes({
                      position: {
                        ...MouseModel.position,
                        x: MouseModel.position.x + (lastBarIndex - oldMaxBarIndex),
                      },
                    })
                  }

                  pane.getAllElements().forEach((element) => {
                    if (element.isDrawing()) {
                      element.instance?.updateScales()
                    }
                  })
                })
              })

              perfIndicatorInstances.forEach((instance) => {
                void instance.compute(true)
              })

              setTimeout(() => {
                handleChartChange(chartModelUnwatched)
              })
            })
        }
      })

      lastZoomAndScrollRef.current = { zoomFactor: chartModel.zoomFactor, leftOffset: chartModel.leftOffset }
    }

    const throttledCheckAndLoadOlderData = throttle(checkAndLoadOlderData, 250)

    const handleChartChange = (chartModel: Chart = chartModelUnwatched, event?: string) => {
      if (!getIsMounted() || event === 'destroy') {
        return
      }

      const chartLayout = chartModel.chart_layout()
      const barWidth = getBarWidthWithMargin({
        chartLayout,
        zoomFactor: chartModel.zoomFactor,
      })
      const barsBufferCacheKey = `${barWidth}-${chartModel.zoomFactor}`

      if (barsBufferCache.current.key !== barsBufferCacheKey) {
        const quoteBarsBufferAndCount = chartModel.getQuoteBarsBufferAndCount()
        barsBufferCache.current = {
          key: barsBufferCacheKey,
          isLoadAll: quoteBarsBufferAndCount.dateFrom === undefined && quoteBarsBufferAndCount.barsCount === undefined,
          bufferInPx: (quoteBarsBufferAndCount.leftBuffer ?? 0) * barWidth,
        }
      }

      const mainQuote = chartModel.quote()
      wrapperRef.current?.style.setProperty(
        '--leftOffset',
        `${Math.min(mainQuote.barIndex[0] * barWidth + chartModel.leftOffset, CHART_BARS_BUFFER_IN_PX)}px`
      )

      throttledCheckAndLoadOlderData({ chartLayout, chartModel, mainQuote, barWidth })
    }

    const handleChartIndicatorOverlayChange = (chartModel: Chart = chartModelUnwatched) => {
      if (!getIsMounted()) {
        return
      }

      const perfIndicatorInstances = chartModel
        .getAllElements()
        .filter(({ instance }) => instance.type === IndicatorType.Perf)
        .map((element) => element.instance as unknown as PricePerformance)
      const perfIndicatorTickers = perfIndicatorInstances.flatMap((instance) =>
        Object.values(instance.quotes).map(({ ticker }) => ticker)
      )

      perfIndicatorsCacheRef.current = {
        perfIndicatorInstances,
        perfIndicatorTickers,
      }

      const quoteFetchBufferConfig = getChartQuoteFetchBufferConfig({
        panes: chartModel.getAllPanes().map((pane) => pane.toConfig() as ChartConfigChartPane),
        timeframe: chartModel.timeframe,
      })

      barsBufferCache.current = DEFAULT_BARS_BUFFER_CACHE

      if (
        quoteFetchBufferConfig.barsBuffer !== chartModel.quoteFetchBufferConfig?.barsBuffer ||
        quoteFetchBufferConfig.dateFrom !== chartModel.quoteFetchBufferConfig?.dateFrom
      ) {
        chartModel.updateAttribute('quoteFetchBufferConfig', quoteFetchBufferConfig)
      }

      handleChartChange(chartModelUnwatched)
    }

    handleChartIndicatorOverlayChange()

    chartModelUnwatched.bind(INDICATOR_OVERLAY_CHANGE_EVENTS, handleChartIndicatorOverlayChange)
    chartModelUnwatched.bind(CHANGE_EVENT, handleChartChange)

    return () => {
      chartModelUnwatched.unbind(INDICATOR_OVERLAY_CHANGE_EVENTS, handleChartIndicatorOverlayChange)
      chartModelUnwatched.unbind(CHANGE_EVENT, handleChartChange)
      throttledCheckAndLoadOlderData.cancel()
    }
  }, [chartModelUnwatched, quoteModel.hasReachedEnd, getIsMounted])

  if (quoteModel.hasReachedEnd || quoteModel.close.length === 0) {
    return null
  }

  return (
    <div
      ref={wrapperRef}
      className={classnames(
        'pointer-events-none absolute left-0 top-0 z-50 flex h-full w-[100px] items-center justify-start overflow-hidden'
      )}
      style={{ left: chartModel.chart_layout().settings.ChartSettings.left.width }}
    >
      <div style={{ transform: `translateX(calc(-58px + var(--leftOffset, 0px))` }}>
        <Spinner width={16} />
      </div>
    </div>
  )
}
